import { IconArrowUp } from "@tabler/icons-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  type SyntheticEvent,
} from "react";
import { twMerge } from "tailwind-merge";
import { useAutoResizeTextarea } from "../hooks/useAutoResizeTextarea.js";

export interface UserMessageFormProps {
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  onMessageSend: (message: string) => void;
}

export const UserMessageForm: FC<UserMessageFormProps> = (props) => {
  const [uncontrolledValue, setUncontrolledValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const message = props.value ?? uncontrolledValue;

  const onMessageChange = (value: string) => {
    if (props.value !== undefined) {
      props.onValueChange?.(value);
    } else {
      setUncontrolledValue(value);
    }
  };

  const sendMessage = () => {
    const trimmedMessage = message.trim();

    // Make sure the message is not empty
    if (trimmedMessage.length === 0) {
      return;
    }

    props.onMessageSend(trimmedMessage);
    onMessageChange("");
  };

  const handleFormSubmit = (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();

    if (props.isDisabled) {
      return;
    }

    sendMessage();
  };

  const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onMessageChange(event.target.value);
  };

  const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      handleFormSubmit(event);
    }
  };

  useAutoResizeTextarea({ textareaRef, value: message });

  const isEmpty = message.trim().length === 0;

  return (
    <form
      className={twMerge("relative", props.className)}
      onSubmit={handleFormSubmit}
    >
      <textarea
        ref={textareaRef}
        name="message"
        aria-label="Your message"
        placeholder={props.placeholder || "Reply..."}
        value={message}
        onChange={handleTextareaChange}
        onKeyDown={handleTextareaKeyDown}
        rows={1}
        className="block max-h-56 w-full resize-none overflow-y-auto rounded-2xl border-none py-2 pr-10 pl-4 text-foreground placeholder-gray-400 outline outline-1 outline-gray-300 transition-shadow duration-150 focus:outline-2 focus:outline-offset-0 focus:outline-indigo-600"
      />

      <button
        type="submit"
        aria-label="Send message"
        disabled={isEmpty || props.isDisabled}
        className="absolute right-1.5 bottom-1.5 flex size-7 items-center justify-center rounded-full bg-indigo-800 text-white hover:bg-indigo-900 disabled:cursor-not-allowed disabled:bg-indigo-200"
      >
        <IconArrowUp className="size-5" />
      </button>
    </form>
  );
};
